import Text from '@deprecated-ui/common/Text';
import color from '@deprecated-ui/_style/color';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';

import SecondarySpinner from 'global/Loading/SecondarySpinner';
import debounce from 'lodash.debounce';
import { selectors as profileSelector } from 'lib/entities/profile';
import {
  PAGE_SOURCE,
  PAGE_SOURCE_INDEX,
} from 'features/user/analytics/ProfileAnalytics';
import EmptySearch from 'global/StreamWidget/EmptyState/EmptySearch';
import If from 'global/Common/If';
import { ModalFullscreen } from '../styled';
import slice, { effects, selectors } from '../../slice';
import UserItem from './UserItem';
import { ModalFollowSearchInput, UserItemContainer } from './styled';

interface ModalFollowerBase {
  visible: boolean;
  onCancel: () => void;
  [key: string]: any;
}

interface ModalCompanyFollower extends ModalFollowerBase {
  companyId: string | number;
  symbol: string;
  username?: never;
  userId?: never;
}

interface ModalUserFollower extends ModalFollowerBase {
  username: string;
  userId: number;
  symbol?: never;
  companyId?: never;
}

type ModalFollowerProps = ModalCompanyFollower | ModalUserFollower;

const pageSourceIndex = PAGE_SOURCE_INDEX.findIndex(
  (sourceIndex) => sourceIndex === 'Followers',
);

function ModalFollower({
  username,
  userId,
  symbol,
  companyId,
  visible,
  onCancel,
  ...props
}: ModalFollowerProps) {
  const dispatch = useDispatch();
  const visibilityRef = useRef<HTMLDivElement>(null);
  const {
    userFollowers,
    companyFollowers,
    isLoadingFollower: isLoading,
    followersPagination: pagination,
  } = useSelector(selectors);
  const { more, page, search: keyword } = pagination;
  const { data: dataProfile } = useSelector(profileSelector);
  const [isSensorVisible, setSensorVisible] = useState(false);

  const handleSensorChange = (isVisible = false) => {
    setSensorVisible(isVisible);
  };

  const handleGetFollowers = useCallback(async () => {
    if (!isSensorVisible || !more || isLoading) return;

    dispatch(
      effects.getFollowers({
        companyid: companyId,
        userid: userId,
        keyword,
        page,
      }),
    );
  }, [isSensorVisible, isLoading, page, more, keyword, userId, companyId]);

  useEffect(() => {
    if (visible) {
      handleGetFollowers();
    } else {
      dispatch(slice.actions.resetFollowersState());
    }
  }, [visible, handleGetFollowers]);

  const searchFollowers = useCallback(
    debounce((search?: string) => {
      dispatch(slice.actions.searchFollowers(search));
      setSensorVisible(true);
    }, 500),
    [],
  );
  const modalTitle = username || symbol;

  const dataFollowers = companyId
    ? companyFollowers[companyId]
    : userFollowers[userId];

  const followers = dataFollowers || [];

  if (!visible) return null;

  return (
    <ModalFullscreen
      {...props}
      // @ts-expect-error
      noBorder
      visible={visible}
      onCancel={onCancel}
      footer={null}
      type="fullscreen"
      maskClosable={false}
      title={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <Text size="20px" family="bold" align="center" color={color.suvaGray}>
          {modalTitle} is followed by
        </Text>
      }
    >
      <ModalFollowSearchInput
        placeholder="Search"
        onChange={({ target: { value } }) =>
          searchFollowers(value || undefined)
        }
        onClear={() => dispatch(slice.actions.searchFollowers())}
      />
      <UserItemContainer ref={visibilityRef}>
        {followers.map((follower) => (
          <UserItem
            pageSource={PAGE_SOURCE.followers}
            pageSourceIndex={pageSourceIndex}
            user={follower}
            key={follower.id}
            activeUserId={Number(dataProfile?.id)}
          />
        ))}
        <SecondarySpinner isLoading={isLoading} />
        <VisibilitySensor
          containment={visibilityRef.current}
          onChange={handleSensorChange}
          scrollCheck
        >
          <div>&nbsp;</div>
        </VisibilitySensor>
        <If condition={followers.length === 0 && !isLoading}>
          <EmptySearch />
        </If>
      </UserItemContainer>
    </ModalFullscreen>
  );
}

export default ModalFollower;
